import { EmitterBase } from './event_emitter';
import { WebRTCBase } from "./base";
import { ActionMessage, ChannelObject, WebRTCChannel } from "./channel";
import { TimeoutWaiter } from "../common/timeout_waiter";
import { Stats } from "../common/stats";
import { generateString } from "../common/util";
import { createTraceInstance } from "../common/open_telemery";
import { AppError } from '../common/app_error';
import { SpanStatusCode } from '@opentelemetry/api';
import Cookies from 'js-cookie';
import { VideoCodec } from './rtc_diagnosis';
export var OfferStatus;
(function (OfferStatus) {
    OfferStatus[OfferStatus["Start"] = 1] = "Start";
    OfferStatus[OfferStatus["Reconnect"] = 2] = "Reconnect";
    OfferStatus[OfferStatus["Add"] = 3] = "Add";
    OfferStatus[OfferStatus["Remove"] = 4] = "Remove";
})(OfferStatus || (OfferStatus = {}));
export var CandidateType;
(function (CandidateType) {
    CandidateType["None"] = "none";
    CandidateType["Turn"] = "turn";
    CandidateType["P2P"] = "p2p";
})(CandidateType || (CandidateType = {}));
export class WebRTC extends EmitterBase {
    constructor(params, settings) {
        super();
        this.localCandidates = [];
        this.remoteCandidates = [];
        this.idMapMid = new Map();
        this.lastReconnectMillis = 0;
        this.started = false;
        this.closed = false;
        this.workers = new Map();
        this.debugging = false;
        this.workerDebug = false;
        this.actionWaiter = new TimeoutWaiter('WebRTC', WebRTC.ACTION_TIMEOUT);
        this.selectedVideoCodec = VideoCodec.H264;
        this.connId = settings.connId;
        const config = {
            iceServers: [params.iceServer]
        };
        if (params.peerConfig) {
            for (const [k, v] of Object.entries(params.peerConfig)) {
                config[k] = v;
            }
        }
        this.errorHandler = settings.errorHandler;
        if (settings.videoCodec) {
            this.selectedVideoCodec = settings.videoCodec;
        }
        if (settings.debugging) {
            this.debugging = true;
        }
        if (settings.workerDebug) {
            this.workerDebug = true;
        }
        if (settings.featureSettings) {
            this.featureSettings = settings.featureSettings;
        }
        this.stats = new Stats('webrtc', this.debugging);
        const userId = Cookies.get('userId');
        // peerConn
        try {
            this.peerConn = new RTCPeerConnection(config);
            this.peerConn.onnegotiationneeded = this.onNegotiationNeeded.bind(this);
            this.peerConn.onicecandidate = this.onIceCandidate.bind(this);
            this.peerConn.oniceconnectionstatechange = this.onIceConnectionStateChange.bind(this);
            this.peerConn.ondatachannel = this.onDataChannel.bind(this);
            this.peerConn.onsignalingstatechange = this.onSignalingStateChange.bind(this);
            this.peerConn.onconnectionstatechange = this.onConnectionStateChange.bind(this);
        }
        catch (e) {
            console.error(e);
            this.emitClose();
        }
        this.tracerProvider = createTraceInstance({ connectId: this.connId, userId: userId, codec: this.selectedVideoCodec }, this.debugging);
    }
    logDebug(message, ...args) {
        if (this.debugging) {
            console.debug(message, ...args);
        }
    }
    getWorker(id) {
        if (!this.workers.has(id)) {
            // DO NOT DELETE OR MODIFY THIS COMMENT
            // This is a workaround for the Webpack 5 issue with worker-loader
            /* start of builder code
            //@ts-ignore
            const worker = workerMaker(workerSource, { name: "webrtc", type: 'module' })
            end of builder code */
            // DO NOT CHANGE THIS LINE
            const worker = new Worker(/* webpackChunkName: "worker" */ new URL('./worker', import.meta.url), { name: "webrtc", type: 'module' });
            worker.onerror = (e) => {
                console.error('error: message=%s, filename=%s, lineno=%s', e.message, e.filename, e.lineno);
            };
            worker.onmessage = (e) => {
                this.onMessage(e.data);
            };
            const codecPort = this.createVideoCodecWorker().port;
            worker.postMessage({ type: WebRTCBase.ACTION_INIT_CODEC, args: { codecPort } }, [codecPort]);
            this.workers.set(id, worker);
            return worker;
        }
        else {
            return this.workers.get(id);
        }
    }
    onMessage(data) {
        // let label: string = null, channelObj: ChannelObject = null
        switch (data.type) {
            case WebRTCBase.ACTION_CHANNEL_OBJECT:
                // ({label, channelObj} = data.args)
                // this.onDataChannelObject(label, channelObj)
                return true;
            case WebRTCBase.ACTION_CHANNEL_CLOSE:
                // ({label} = data.args)
                // // this.onDataChannelClose(label)
                return true;
            case WebRTCBase.ACTION_ERROR:
                // TODO: process error here
                //this.close()
                this.emitEvent(WebRTC.EVENT_DECODER_ERROR, data.args);
                return true;
            case WebRTCBase.ACTION_FIRST_FRAME:
                this.emitEvent(WebRTCBase.ACTION_FIRST_FRAME, data.args);
                return true;
        }
        return false;
    }
    createVideoCodecWorker() {
        const wokerUrl = new URL('./video_codec/shared_worker', import.meta.url);
        // DO NOT DELETE OR MODIFY THIS COMMENT
        // This is a workaround for the Webpack 5 issue with worker-loader
        /* start of builder code
         return sharedWorkerMaker(sharedWorkerSource,{
             //@ts-ignore
            name: (window.__wasmURL || "video_codec") + "," + wokerUrl.pathname,
            type: 'module',
         })
        end of builder code */
        return new SharedWorker(/* webpackChunkName: "shared-worker" */ new URL('./video_codec/shared_worker', import.meta.url), {
            //@ts-ignore
            name: (window.__wasmURL || "video_codec") + "," + wokerUrl.pathname,
            type: 'module',
        });
    }
    start() {
        if (this.started) {
            console.warn('started already: connId=' + this.connId);
            return false;
        }
        this.tracerProvider.startAllSpan();
        // transceivers
        this.peerConn.addTransceiver('video');
        this.peerConn.addTransceiver('audio');
        this.started = true;
        this.stats.start({ key: WebRTC.STATS_CONNECTION });
        // messageChannel
        // the front-end actively creates a message channel
        const messageChannel = this.peerConn.createDataChannel(WebRTC.CHAN_ACTION_NAME);
        messageChannel.onerror = e => {
            console.error('error in channel: connId=%s, error=%s', this.connId, e);
        };
        this.messageChannel = new WebRTCChannel({
            connId: this.connId,
            channel: messageChannel,
            onObject: this.onDataChannelAction.bind(this),
            onClose: this.onDataChannelClose.bind(this),
            onOpen: (channel) => {
                this.emitEvent(WebRTC.EVENT_MESSAGE_OPEN, channel);
            }
        });
        //stop error pop up
        this.createOffer(OfferStatus.Start).catch();
        return true;
    }
    async createOffer(status, func) {
        if (!this.started)
            throw new Error("It is not started yet");
        console.debug("beforecreateOffer: connId=%s status=%s", this.connId, status);
        const offer = await this.peerConn.createOffer();
        if (this.peerConn.signalingState != WebRTC.SS_STABLE) {
            console.warn("The connection isn't stable yet; postponing...: connId=" + this.connId);
        }
        console.debug("createOffer: connId=%s status=%s", this.connId, status);
        try {
            if (status == OfferStatus.Reconnect) {
                await this.peerConn.setLocalDescription(offer);
                this.emitEvent(WebRTC.EVENT_RECONNECT, this, offer, func);
            }
            else if (status == OfferStatus.Start) {
                await this.peerConn.setLocalDescription(offer);
                this.emitEvent(WebRTC.EVENT_CREATE_OFFER, this, offer, this.tracerProvider.traceHeaders);
            }
            else if (status == OfferStatus.Add) {
                await this.peerConn.setLocalDescription(offer);
            }
        }
        catch (e) {
            console.error(e);
            this.emitError(e);
            throw e;
        }
        return offer.sdp;
    }
    onConnectionStateChange(event) {
        if (this.debugging) {
            const now = new Date();
            console.debug(now.toISOString() + ': onConnectionStateChange: connectionState=' + this.peerConn.connectionState + ", connId=" + this.connId);
        }
        switch (this.peerConn.connectionState) {
            case WebRTC.CS_CONNECTED:
                this.stats.end({ key: WebRTC.STATS_CONNECTION });
                this.emitEvent(WebRTC.EVENT_CONNECTED, this);
                this.tracerProvider.endPeerConnection(SpanStatusCode.OK);
                break;
            case WebRTC.CS_DISCONNECTED:
                this.tracerProvider.endAllSpan(SpanStatusCode.ERROR, { reason: this.peerConn.connectionState });
                for (let worker of this.workers.values()) {
                    console.log("reset first frame");
                    worker.postMessage({ type: WebRTCBase.ACTION_RESET_FIRST_FRAME });
                }
                this.reconnect();
                break;
            case WebRTC.CS_CLOSED:
            case WebRTC.CS_FAILED:
                this.emitClose();
                break;
        }
    }
    async onNegotiationNeeded(event) {
        if (this.debugging)
            console.debug("onNegotiationNeeded: connId=" + this.connId);
    }
    onIceConnectionStateChange(event) {
        if (this.debugging) {
            console.debug("onIceConnectionStateChange: state=" + this.peerConn.iceConnectionState + ", connId=" + this.connId);
        }
        switch (this.peerConn.iceConnectionState) {
            case "connected":
                this.tracerProvider.endIceConnection(SpanStatusCode.OK);
                this.checkCandidateType();
                break;
            case "closed":
            case "failed":
                this.tracerProvider.endIceConnection(SpanStatusCode.ERROR);
                this.emitClose();
                break;
        }
    }
    async doAddIceCandidates() {
        while (true) {
            if (this.remoteCandidates.length == 0 || this.peerConn.signalingState !== WebRTC.SS_STABLE)
                break;
            const candidate = this.remoteCandidates.shift();
            await this.peerConn.addIceCandidate(candidate);
        }
    }
    doOnIceCandidates() {
        if (this.peerConn.signalingState === WebRTC.SS_STABLE) {
            try {
                this.emitEvent(WebRTC.EVENT_ICE_CANDIDATES, this);
            }
            catch (e) {
                this.emitError(e);
            }
        }
    }
    onIceCandidate(event) {
        if (this.debugging) {
            console.debug("onIceCandidate: candidate=" + event.candidate?.candidate + ", connId=" + this.connId);
        }
        if (event.candidate) {
            this.localCandidates.push(event.candidate);
            this.doOnIceCandidates();
        }
    }
    async addIceCandidates(candidates) {
        if (this.debugging) {
            console.debug("addIceCandidates: candidates=" + JSON.stringify(candidates) + ", connId=" + this.connId);
        }
        candidates.forEach((candidate) => {
            const iceCandidate = new RTCIceCandidate(candidate);
            this.remoteCandidates.push(iceCandidate);
        });
        await this.doAddIceCandidates();
    }
    shiftIceCandidates(limit) {
        if (limit <= 0 || limit > this.localCandidates.length)
            limit = this.localCandidates.length;
        return this.localCandidates.splice(0, limit);
    }
    onSignalingStateChange(event) {
        if (this.debugging) {
            console.debug("onSignalingStateChange: signalingState=" + this.peerConn.signalingState + ", connId=" + this.connId);
        }
        switch (this.peerConn.signalingState) {
            case "closed":
                this.emitClose();
                break;
            case WebRTC.SS_STABLE:
                this.doAddIceCandidates().catch(console.log);
                this.doOnIceCandidates();
                break;
        }
    }
    onDataChannel(event) {
        if (this.debugging)
            console.debug("onDataChannel: label=" + event.channel.label + ", connId=" + this.connId);
        /* for worker when RTCDataChannel is transferable
        let worker = this.getWorker()
        worker.postMessage({
            type: WebRTCBase.ACTION_CHANNEL,
            args: {connId: this.connId, channel: event.channel}
        }, [event.channel])
         */
        const webRTCChannel = new WebRTCChannel({
            connId: this.connId, channel: event.channel,
            onObject: this.onDataChannelObject.bind(this), onClose: this.onDataChannelClose.bind(this)
        });
    }
    onDataChannelObject(channel, channelObject) {
        if (this.debugging) {
            console.debug('onDataChannelObject: channelObject=%s, connId=%s', channelObject.description(), this.connId);
        }
        this.emitEvent(WebRTC.CHAN_MSG_PREFIX + channel.label, channel, channelObject);
    }
    onActionMessage(channel, channelObject) {
        if (this.debugging) {
            console.debug('onActionMessage: channelObject=%s, label=%s, connId=%s', channelObject.description(), channel.label, this.connId);
        }
        const ret = ActionMessage.fromChannelObject(channelObject);
        if (!ret.actionMessage) {
            console.error('onActionMessage: error=%s, label=%s, connId=%s', ret.error, channel.label, this.connId);
            return ret.actionMessage;
        }
        this.actionWaiter.resetTimeout({
            id: ret.actionMessage.id,
            callbackObject: { error: ret.error, result: ret.actionMessage }
        });
        return ret.actionMessage;
    }
    onDataChannelAction(channel, channelObject) {
        const actionMessage = this.onActionMessage(channel, channelObject);
        if (actionMessage)
            this.emitEvent(WebRTC.EVENT_ACTION, channel, actionMessage);
    }
    doSendAction(actionMessage) {
        if (this.debugging)
            console.debug('doSendAction: message=%s, connId=%s', JSON.stringify(actionMessage), this.connId);
        const error = (actionMessage.channel || this.messageChannel).sendJsonData(actionMessage);
        if (error?.name === ChannelObject.CHANNEL_BUFFER_FULL)
            this.emitClose();
        return error;
    }
    sendActionMessage(action, args, callback, timeout, channel) {
        const actionMessage = new ActionMessage(generateString(31), action, args);
        actionMessage.channel = channel;
        return this.sendAction(actionMessage, (error, result) => {
            if (error) {
                console.debug("%s.reply: error=%s, result=%s, connId=%s", action, error, JSON.stringify(result), this.connId);
            }
            if (this.errorHandler) {
                callback?.(this.errorHandler?.(error), result);
            }
            else {
                callback?.(error, result);
            }
        }, timeout);
    }
    // return Error deprecated, will not use in the future
    sendAction(actionMessage, callback, timeout) {
        const error = this.doSendAction(actionMessage);
        if (!error) {
            this.actionWaiter.setTimeout({ id: actionMessage.id, callback, timeout }, actionMessage.act);
        }
        else {
            callback?.(AppError.fromError(error), null);
        }
        return null;
    }
    onDataChannelClose(channel, channelObject) {
        this.emitEvent(WebRTC.CHAN_CLOSE_PREFIX + channel.label, channel);
    }
    reconnect(func) {
        if (Date.now() - this.lastReconnectMillis < WebRTC.RECONNECT_INTERVAL) {
            console.warn('ignore reconnect: connId=%s', this.connId);
            func?.();
            return;
        }
        this.peerConn.restartIce();
        this.lastReconnectMillis = Date.now();
        this.createOffer(OfferStatus.Reconnect, func).catch(e => this.emitError(e));
        this.tracerProvider.startAllSpan(true);
    }
    emitError(e) {
        try {
            this.emitEvent(WebRTC.EVENT_ERROR, e);
            this.errorHandler?.(e);
        }
        catch (e) {
        }
        console.warn('emitError: error=%s, connId=%s', e, this.connId);
        this.emitClose();
    }
    async putAnswer(answer) {
        if (this.debugging)
            console.debug("putAnswer: connId=" + this.connId);
        if (this.closed) {
            console.warn('ignore putAnswer after closed: connId=%s', this.connId);
            return;
        }
        this.tracerProvider.endOffer(SpanStatusCode.OK);
        const desc = new RTCSessionDescription({ type: 'answer', sdp: answer });
        return this.peerConn.setRemoteDescription(desc);
    }
    isConnected() {
        return this.peerConn.connectionState === WebRTC.CS_CONNECTED;
    }
    async checkCandidateType() {
        console.warn("Not implement of checkCandidateType: connId=" + this.connId);
    }
    close() {
        this.emitClose();
    }
    emitClose() {
        if (this.closed)
            return;
        this.closed = true;
        this.doClose();
        this.emitEvent(WebRTC.EVENT_CLOSE, this);
    }
    doClose() {
        if (this.peerConn && this.peerConn?.connectionState != WebRTC.CS_CLOSED) {
            this.peerConn.close();
            this.tracerProvider.endAllSpan(SpanStatusCode.ERROR, { reason: this.peerConn.connectionState });
        }
        this.workers.forEach((worker) => {
            worker.terminate?.();
        });
    }
    getSelectedVideoCodec() {
        return this.selectedVideoCodec;
    }
}
WebRTC.CHAN_MSG_PREFIX = 'chan_msg:';
WebRTC.CHAN_CLOSE_PREFIX = 'chan_close:';
WebRTC.CHAN_ACTION_NAME = 'message';
WebRTC.CHAN_OPEN_SUFFIX = "_open";
WebRTC.EVENT_CLOSE = 'close';
WebRTC.EVENT_ERROR = 'error';
WebRTC.EVENT_DECODER_ERROR = 'decoder_error';
WebRTC.EVENT_CONNECTED = 'connected';
WebRTC.EVENT_CREATE_OFFER = 'create_offer';
WebRTC.EVENT_RECONNECT = 'reconnect';
WebRTC.EVENT_ICE_CANDIDATES = 'ice_candidates';
WebRTC.EVENT_MESSAGE_OPEN = 'message_open';
WebRTC.EVENT_ACTION = WebRTC.CHAN_MSG_PREFIX + WebRTC.CHAN_ACTION_NAME;
WebRTC.EVENT_UNRELIABLE_MESSAGE = WebRTC.CHAN_MSG_PREFIX + 'unreliable_message'; // no more than One MTU is allowed
WebRTC.SS_STABLE = 'stable';
WebRTC.CS_CONNECTED = 'connected';
WebRTC.CS_DISCONNECTED = 'disconnected';
WebRTC.CS_FAILED = 'failed';
WebRTC.CS_CLOSED = 'closed';
WebRTC.ACTION_TIMEOUT = 5000;
WebRTC.RECONNECT_INTERVAL = 5000;
WebRTC.STATS_CONNECTION = 'connection';
// legacy
WebRTC.LEGACY_WORKER_ID = 'uniqueId';
